/*
 * Derive the *currently displayed* rotation per page by folding the rotate-page
 * edits over each page's original rotation. The viewer renders from the
 * original bytes (pdf.js), so without this the canvas + page bar would ignore
 * pending rotate edits, and repeated rotate clicks couldn't accumulate.
 */

import type { Edit, PdfPage } from '@pdf-ticket/pdf-core';

export type Rotation = 0 | 90 | 180 | 270;

/**
 * Map page index → effective rotation. Pages start at their original rotation;
 * each rotate-page edit (in order) overrides it, so the last one wins. Edits
 * targeting an unknown page index are ignored.
 */
export function effectiveRotations(
  pages: ReadonlyArray<PdfPage>,
  edits: ReadonlyArray<Edit>,
): Map<number, Rotation> {
  const map = new Map<number, Rotation>();
  for (const p of pages) map.set(p.index, p.rotation);
  for (const e of edits) {
    if (e.kind === 'rotate-page' && map.has(e.page)) map.set(e.page, e.to);
  }
  return map;
}

/** Next clockwise quarter turn (0→90→180→270→0). */
export const rotateCW = (deg: Rotation): Rotation => (((deg + 90) % 360) as Rotation);
